'use client'

import { HomeContainer } from '@/components/common'
import { Button } from '@/components/ui/button'
import { useIsLoggedIn } from '@/hooks/react-query/user/use-user'
import { ImageHelper } from '@/lib/helpers/image.helper'

import { sanitize } from '@/lib/helpers/sanitize'
import { cn, formatNumber, normalizeVietnamese } from '@/lib/utils'
import { ClubMemberActivity } from '@/types/club'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'

const config = {
  title: 'awareness.title',
  memberText: 'awareness.memberText',
  items: [
    {
      title: 'awareness.items.0.title',
      link: '/coolclub#referral-friend-section?itm_source=awareness',
      description: 'awareness.items.0.description',
      icon: {
        url: '/image/March2025/mceclip4_90.png',
        width: 18,
        height: 26,
      },
    },
    {
      title: 'awareness.items.1.title',
      // description: '(X2 vào thứ 6)',
      link: '',
      icon: {
        url: '/image/November2025/mceclip0_63.png',
        width: 32,
        height: 26,
      },
    },
    {
      title: 'awareness.items.2.title',
      description: 'awareness.items.2.description',
      link: '',
      icon: {
        url: '/image/March2025/mceclip2_83.png',
        width: 22,
        height: 26,
      },
    },
  ],
  recentText: 'awareness.recentText',
  linkClub: '/coolclub?itm_source=awareness',
}

type Props = {
  data: ClubMemberActivity[]
  className?: string
  total: number
}

export const CoolClubAwarenessContentDesktop: React.FC<Props> = ({
  data,
  className,
  total,
}) => {
  const isLoggedIn = useIsLoggedIn()
  const t = useTranslations('button')
  const tAwareness = useTranslations()

  const splitArray = (arr: ClubMemberActivity[], idx: number) => {
    const chunkSize = Math.ceil(arr.length / idx)
    const start = (idx - 1) * chunkSize
    return arr.slice(start, start + chunkSize)
  }


  return (
    <HomeContainer>
      <div
        className={cn(
          'mb-10 rounded-2xl py-5 lg:mb-15 lg:flex lg:pt-11',
          'bg-neutral-100',
          className
        )}
      >
        <div className='border-b border-b-neutral-300 px-8 pb-11 lg:flex-1 lg:border-0 lg:px-12'>
          <h4 className='mb-4 font-criteria text-base font-bold uppercase text-neutral-700 lg:text-2xl'>
            {tAwareness(config.title)}
            <span className='text-primary-600'>
              {' '}
              {formatNumber(total)}
            </span>{' '}
            {tAwareness(config.memberText)}
          </h4>

          <ul className='space-y-2 lg:flex lg:items-stretch lg:space-x-4 lg:space-y-0'>
            {config.items.map((item) => (
              <li
                key={item.title}
                className='relative flex-1 overflow-hidden rounded-lg bg-primary-500 px-4 py-2 lg:h-32'
                // className='relative overflow-hidden rounded-lg bg-primary-500 px-4 py-2 lg:h-32 lg:w-44 lg:px-5 lg:py-5 2xl:w-64'
              >
                <Link
                  href={item.link}
                  className='flex h-full items-start justify-between text-light'
                  ga-tracking-value={`coolclub-awareness-desktop__privilege__${normalizeVietnamese(tAwareness(item.title).toLowerCase().replace(/\s+/g, '-'))}`}
                  ga-tracking-label={tAwareness(item.title)}
                >
                  <p className='font-criteria text-sm font-semibold leading-4.5 text-light lg:text-xl lg:leading-6'>
                    {tAwareness(item.title)} <br />
                    {item.description && tAwareness(item.description)}
                  </p>
                  <Image
                    src={ImageHelper.getImageUrl(item.icon.url)}
                    alt={
                      tAwareness(item.title) +
                      (item.description ? tAwareness(item.description) : '')
                    }
                    width={item.icon.width}
                    height={item.icon.height}
                    className='max-w-8 lg:absolute lg:bottom-5 lg:right-5'
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className='relative mt-4 overflow-hidden px-8 before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:bg-gradient-to-b before:from-transparent before:via-neutral-900/20 before:to-transparent max-lg:before:hidden lg:basis-5/12 lg:px-12'>
          <p className='mb-3 text-center font-criteria text-xl font-bold uppercase text-neutral-800 lg:font-medium'>
            {tAwareness(config.recentText)}
          </p>

          <div className='relative overflow-hidden before:absolute before:-left-9 before:top-0 before:z-[1] before:h-full before:w-1/5 before:rotate-180 before:bg-[linear-gradient(270deg,_#F3EFFF_28.79%,_rgba(243,239,255,0)_100%)] after:absolute after:-right-9 after:top-0 after:z-[1] after:h-full after:w-1/5 after:rotate-180 after:bg-[linear-gradient(270deg,_rgba(243,239,255,0)_0%,_#F3EFFF_51.21%)] lg:before:hidden lg:after:hidden'>
            {Array.from({ length: 2 }).map((_, idx) => {
              const activities = splitArray(data, idx + 1)

              return (
                <ul
                  className={`flex gap-3 whitespace-nowrap ${idx === 1 ? 'animate-marquee2' : 'animate-marquee'}`}
                  key={idx}
                >
                  {activities.map((activity, idxActivity) => (
                    <li
                      className='font-sans text-base text-neutral-600'
                      key={idxActivity}
                      dangerouslySetInnerHTML={{
                        __html: sanitize(activity.content),
                      }}
                    ></li>
                  ))}
                </ul>
              )
            })}
          </div>

          {isLoggedIn ? (
            <Button
              className='mt-4 w-full uppercase lg:mt-6 lg:h-10'
              variant={'black'}
              asChild
            >
              <Link
                href={config.linkClub}
                className='uppercase'
                ga-tracking-value='coolclub-awareness-desktop__explore-coolclub'
                ga-tracking-label={t('explore_coolclub')}
              >
                {t('explore_coolclub')}
                <Image
                  src='/icons/icon-arrow-line-right.svg'
                  width={15}
                  height={16}
                  alt='icon arrow right'
                  className='transition-transform duration-200 group-hover:translate-x-1'
                />
              </Link>
            </Button>
          ) : (
            <Button
              className='mt-4 w-full lg:mt-6 lg:h-10'
              asChild
              variant={'black'}
            >
              <Link
                href={config.linkClub}
                className='uppercase'
                ga-tracking-value='coolclub-awareness-desktop__join-coolclub'
                ga-tracking-label={t('join_coolclub')}
              >
                {t('join_coolclub')}
                <Image
                  src='/icons/icon-arrow-line-right.svg'
                  width={15}
                  height={16}
                  alt='icon arrow right'
                  className='transition-transform duration-200 group-hover:translate-x-1'
                />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </HomeContainer>
  )
}
